import { Component, OnInit, Input, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AmbassadorProgramCompanyUserService } from '../../../../services/ambassador-program-company-user.service';
import { AmbassadorProgramCompanyUserDTO, AmbassadorProgramCompanyUserPostDTO } from '../../../../models/ambassador-program-company-user-dto';
import { NgxDropzoneModule } from 'ngx-dropzone';
import Swal from 'sweetalert2';
import { Helpers } from '../../../../helpers/helpers';

@Component({
    selector: 'ambassador-reservation-form',
  standalone: true,
  templateUrl: './ambassador-reservation-form.component.html',
  styleUrls: ['./ambassador-reservation-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule,
    ReactiveFormsModule,
    NgxDropzoneModule]
})

export class AmbassadorReservationFormComponent implements OnInit {

    @Input() idSchedule: string;
    @Input() timePreference: string;
    @Input() idVehicle: string;
    @Output() backToSummary = new EventEmitter<void>();
    @Output() reservationCompleted = new EventEmitter<void>();

    userInfo: AmbassadorProgramCompanyUserDTO;

    reservationForm: FormGroup;
    selectedLicenseFiles: boolean = false;
    submitted = false;

    licenseFrontFiles: File[] = [];
    licenseBackFiles: File[] = [];

    constructor(private userService: AmbassadorProgramCompanyUserService,
        private helper: Helpers
    ) {
    }

    ngOnInit() {
        const dniPattern = /^(?:\d{8}[A-Z]|[XYZ]\d{7}[A-Z])$/;
        const phonePattern = /^(?:\+34|0034)?(?:\s|-)*[6-9](?:(?:\s|-)*\d){8}$/;
        const postalCodePattern = /^(?:0[1-9]|[1-4]\d|5[0-2])\d{3}$/;
        this.reservationForm = new FormGroup({
            IdCompany: new FormControl(null),
            IdProgram: new FormControl(null),
            Email: new FormControl(null),
            Name: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
            Dni: new FormControl(null, [Validators.required, Validators.maxLength(9), Validators.pattern(dniPattern)]),
            Phone: new FormControl(null, [Validators.required, Validators.pattern(phonePattern)]),
            Address: new FormControl(null, [Validators.required, Validators.maxLength(200)]),
            Location: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
            Cp: new FormControl(null, [Validators.required, Validators.maxLength(5), Validators.pattern(postalCodePattern)]),
            Province: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
            Radio: new FormControl(null, [Validators.maxLength(100)]),
            Comments: new FormControl(null, [Validators.maxLength(500)]),
            dataProtection: new FormControl(false, [Validators.requiredTrue])
        });
        this.getUserInfo();
    }

    onDniInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const cleanedValue = input.value.toUpperCase().replace(/[^0-9A-Z]/g, '');
        if (cleanedValue !== input.value) {
          input.value = cleanedValue;
        }
        this.reservationForm.get('Dni')?.setValue(cleanedValue, { emitEvent: false });
    }

    onPhoneInput(event: Event) {
        const input = event.target as HTMLInputElement;
        let cleanedValue = input.value.replace(/[^0-9+\s-]/g, '');
        const hasLeadingPlus = cleanedValue.startsWith('+');
        cleanedValue = cleanedValue.replace(/\+/g, '');
        cleanedValue = hasLeadingPlus ? `+${cleanedValue}` : cleanedValue;

        if (cleanedValue !== input.value) {
          input.value = cleanedValue;
        }
        this.reservationForm.get('Phone')?.setValue(cleanedValue, { emitEvent: false });
    }

    onPostalCodeInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const cleanedValue = input.value.replace(/\D/g, '');
        if (cleanedValue !== input.value) {
          input.value = cleanedValue;
        }
        this.reservationForm.get('Cp')?.setValue(cleanedValue, { emitEvent: false });
    }

    getUserInfo() {
        this.userService.getProgramCompanyUserData().subscribe(res => {
          if(res){
            this.userInfo = res;
            this.reservationForm.patchValue(this.userInfo);
          }
        });
    }

    onFileLicenseFrontSelect(event) {
        if (this.licenseFrontFiles.length > 0) {
          this.licenseFrontFiles.splice(0, 1);
        }
        this.licenseFrontFiles.push(...event.addedFiles);
    }

    onFileLicenseFrontRemove(event) {
        this.licenseFrontFiles.splice(this.licenseFrontFiles.indexOf(event), 1);
    }

    onFileLicenseBackSelect(event) {
        if (this.licenseBackFiles.length > 0) {
          this.licenseBackFiles.splice(0, 1);
        }
        this.licenseBackFiles.push(...event.addedFiles);
    }

    onFileLicenseBackRemove(event) {
        this.licenseBackFiles.splice(this.licenseBackFiles.indexOf(event), 1);
    }

    save() {
        this.submitted = true;

        if (
          (
            (this.licenseFrontFiles?.length ?? 0) > 0 ||
            !!this.userInfo?.License1File
          ) &&
          (
            (this.licenseBackFiles?.length ?? 0) > 0 ||
            !!this.userInfo?.License2File
          )
        ) {
          this.selectedLicenseFiles = true;
        }

        if (this.reservationForm.invalid || !this.selectedLicenseFiles) {
            return;
        }

        let postModel: AmbassadorProgramCompanyUserPostDTO = this.reservationForm.value;

        Swal.fire({
            allowOutsideClick: false,
            text: 'Espere por favor...',
            icon: 'info',
            customClass: {
              popup: 'custom-swal-popup',
              title: 'custom-swal-title',
              htmlContainer: 'custom-swal-html',
              cancelButton: 'custom-swal-cancel',
              confirmButton: 'custom-swal-confirm',
              icon: 'custom-swal-icon'
            }
        });
        Swal.showLoading();

        if (this.licenseFrontFiles.length > 0)
        postModel.License1File = this.licenseFrontFiles[0];

        if (this.licenseBackFiles.length > 0)
        postModel.License2File = this.licenseBackFiles[0];

        postModel.IdReservationSchedule = this.idSchedule;
        postModel.TimePreference = this.timePreference;

        var data = this.helper.getFormData(postModel);
        this.putData(data);
    }

    putData(data: FormData) {
        this.userService.updateLoggedUser(data).subscribe(resp => {
          Swal.close();
          this.reservationCompleted.emit();
        }, (err) => {
          Swal.fire({
            icon: 'error',
            title: 'Error al actualizar la reserva',
            text: 'Ocurrió un error',
            customClass: {
              popup: 'custom-swal-popup',
              title: 'custom-swal-title',
              htmlContainer: 'custom-swal-html',
              cancelButton: 'custom-swal-cancel',
              confirmButton: 'custom-swal-confirm',
              icon: 'custom-swal-icon'
            },
            confirmButtonText: 'Cerrar <i class="fa-solid fa-xmark"></i>'
          });
        });
      }

    onBack() {
        this.backToSummary.emit();
    }

}

